import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import ScheduleEventJobsData from './ScheduleEventJobs';

type EventDay = {
    scheduleDate: string;
    scheduleDateFormatted: string;
    scheduleDateFormattedSmall: string;
    eventJobTotals: EventDayTotals;
    canEdit: boolean;
    isCurrentDay: boolean;
    jobs: EventJob[];
}

type EventJob = {
    id: number;
    jobId: number;
    jobQuoteItemId: number;
    companyId: number;
    scheduledDate: string;
    scheduledBranch: string;
    scheduledBranchId: number;
    truckId: number;
    truck: string;
    eventName: string;
    customerId: number;
    customer: string;
    siteAddress: string;
    deliveryAddress: string;
    deliveryAddressGooglePlaceId: string;
    deliveryAddressLatitude: number;
    deliveryAddressLongitude: number;
    invoiceDescription: string;
    description: string;
    manualItem: boolean;
    type: string;
    typeId: number;
    itemId: number;
    item: string;
    itemShortcut: string;
    itemTypeId: number; 
    quantityScheduled: number;
    quantityType: string;
    offhireDate: string;
    accountChecked: boolean;
    goAhead: boolean;
    callout: boolean;
    showRedNotes: boolean;
    showGreenNotes: boolean;
    scheduleNotes: string;
    status: string;
    statusId: number;
    customerUpdatesRequired: boolean;
    craneTruckRequired: boolean;
    jobOrder: number;
    isPlaceholder: boolean;
    purchaseOrderNumber: string;
    trolley: boolean;
    rowVersionTimestamp: Uint8Array;
    canEdit: boolean;
    canDelete: boolean;
}

type EventDayTotals = {
    eventToiletTotal: number;
    disabledToiletTotal: number;
    hydrationStationTotal: number;
    dayAmountFormatted: string;
}

interface GenericList {
    id: number;
    name: string;
}

interface ItemOrder {
    scheduleId: number;
    jobOrder: number;
    rowVersionTimestamp: Uint8Array;
}

interface TruckListOption {
    id: number;
    branchId: number;
    name: string;
    registrationNumber: string;
    scheduleVehicleId: number;
    teamDisplay: string;
    numberTeamMembers: number;
}

const ScheduleEventData = (props: RouteComponentProps<{}>) => {
    var date = new Date();
    const [loading, setLoading] = React.useState(true);
    const [loadingDay, setLoadingDay] = React.useState(true);
    const [canEdit, setCanEdit] = React.useState(false);
    const [monthOptions, setMonthOptions] = React.useState<GenericList[]>([]);
    const [yearOptions, setYearOptions] = React.useState<GenericList[]>([]);
    const [month, setMonth] = React.useState(date.getMonth() + 1);
    const [year, setYear] = React.useState(date.getFullYear());
    const [dayJobs, setDayJobs] = React.useState<EventDay[]>([]);
    const [scheduleTypes, setScheduleTypes] = React.useState<GenericList[]>([]);
    const [trucks, setTrucks] = React.useState<TruckListOption[]>([]);
    const [systemComanyId, setSystemCompanyId] = React.useState(2);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/User/CheckUserPermission?Permission=ViewEventSchedule&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data == true) {
                getEventSchedule();
            } else if (res.data == false) {
                //redirect to 403 permission denied
                props.history.push("/accessdenied")
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const getEventSchedule = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        setLoadingDay(true);
        axios.get('api/Schedule/GetEventSchedule?month=' + month + "&year=" + year + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setMonthOptions(res.data.monthOptions);
            setYearOptions(res.data.yearOptions);
            setDayJobs(res.data.days);
            setSystemCompanyId(res.data.systemCompanyId);
            setScheduleTypes(res.data.scheduleTypes);
            setTrucks(res.data.trucks);       
            setLoading(false);
            setLoadingDay(false);
            setCanEdit(res.data.canEdit);

            executeScroll();
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const getEventJobs = async (searchMonth: number, searchYear: number, scrollToDay: boolean) => {
        const token = await authService.getAccessToken();

        setLoadingDay(true);
        axios.get('api/Schedule/GetEventJobs?month=' + searchMonth + "&year=" + searchYear, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setDayJobs(res.data);
            setLoadingDay(false);

            if (scrollToDay) {
                executeScroll();
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //refreshes the particular day if something has changed on that day
    const getEventDayJobs = async (scheduleDate: string) => {
        const token = await authService.getAccessToken();

        var days = [...dayJobs];
        var selectedDay = days.find(d => d.scheduleDate == scheduleDate);
        if (selectedDay) {
            axios.get('api/Schedule/GetEventDayJobs?scheduleDate=' + scheduleDate, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                if (selectedDay) {
                    selectedDay.jobs = res.data;
                    setDayJobs(days);
                }
            })
            .catch(error => {
                toast.error(error.message);
            });
        }
    }

    const executeScroll = () => {
        var divToScrollTo = document.getElementById('currentday');
        var header = document.getElementById('header');
        var headerHeight = 0;
        if (header) {
            headerHeight = header.offsetHeight;
        }

        if (divToScrollTo) {
            var options: ScrollToOptions = {
                behavior: "smooth",
                left: 0,
                top: divToScrollTo.offsetTop - headerHeight - 50
            };
            window.scrollTo(options);
        }
    }

    const refresh = () => {
        getEventJobs(month, year, false);
    }

    const resetSearch = () => {
        var date = new Date();
        var currentMonth = (date.getMonth() + 1);
        var currentYear = date.getFullYear();
        setMonth(currentMonth);
        setYear(currentYear);

        getEventJobs(currentMonth, currentYear, true);
    }

    const handleMonthChange = (e: any) => {
        var value = e.target.value * 1;
        setMonth(value);
    }

    const handleYearChange = (e: any) => {
        var value = e.target.value * 1;
        setYear(value);
    }

    const saveDayOrder = (scheduleDate: string, jobs: EventJob[]) => {
        var items: ItemOrder[] = [];
        for (var i = 0; i < jobs.length; i++) {
            var job = jobs[i];
            var item: ItemOrder = {
                scheduleId: job.id,
                jobOrder: job.jobOrder,
                rowVersionTimestamp: job.rowVersionTimestamp
            };
            items.push(item);
        }

        saveOrder(scheduleDate, items);
    }

    const saveOrder = async (scheduleDate: string, items: ItemOrder[]) => {
        const token = await authService.getAccessToken();

        var jobs = [...dayJobs];
        var day = jobs.find(j => j.scheduleDate == scheduleDate);

        var updateJobOrder = {
            scheduleDate: scheduleDate,
            items: items
        };

        axios.post('api/Schedule/SaveEventDayOrder', updateJobOrder, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                if (day) {
                    day.jobs = res.data.jobs;
                    setDayJobs(jobs);
                }
                toast.success("Schedule Saved");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const updateJob = (scheduleDate: string, jobIndex: number, updatedJob: EventJob) => {
        var jobs = [...dayJobs];
        var day = jobs.find(j => j.scheduleDate == scheduleDate);
        var job = day?.jobs[jobIndex];
        job = updatedJob;
        setDayJobs(jobs);
    }

    const updateDay = (scheduleDate: string) => {
        //refreshes the current day only
        getEventDayJobs(scheduleDate);
    }


    const renderDetails = (
        <div>
            <div className={loadingDay ? "" : "hidden"}>
                <p><em>Loading...</em></p>
            </div>
            <div className={loadingDay ? "hidden" : "scheduleTiltCrane marginTop120"}>
                <div id="header" className="schedulenew__search--fixed">
                    <div className="schedulenew__search--fixed-width">
                        <div className="schedulenew__statusBar">
                            <div>
                                <label className="input-group--inline" htmlFor="month">
                                    <select className="select" id="month" name="month" value={month} onChange={(e) => handleMonthChange(e)}>
                                        <option defaultValue="-1"></option>
                                        {monthOptions.map(month =>
                                            <option key={month.id} value={month.id}>{month.name}</option>
                                        )};
                                    </select>
                                </label>
                                <label className="input-group--inline" htmlFor="year">
                                    <select className="select" id="year" name="year" value={year} onChange={(e) => handleYearChange(e)}>
                                        <option defaultValue="-1"></option>
                                        {yearOptions.map(year =>
                                            <option key={year.id} value={year.id}>{year.name}</option>
                                        )};
                                    </select>
                                </label>
                                <button className="defaultbutton defaultbutton__medium margin10" type="button" onClick={refresh}>Refresh</button>
                                <button className="defaultbutton defaultbutton__small margin10" type="button" onClick={resetSearch}>Reset</button>
                            </div>
                        </div>
                        <div className="marginLeft10">
                            <span className="allocate__customerNoteText">* Items in dark red need Customer unlocked.</span>
                        </div>
                        <hr></hr>
                    </div>
                </div>

                {dayJobs.map((day, index) =>
                    <div className="scheduleRow paddingFull10" key={index}>
                        <div id={day.isCurrentDay ? "currentday" : ""}></div>
                        <ScheduleEventJobsData key={index} systemCompanyId={systemComanyId} canEdit={canEdit} scheduleTypes={scheduleTypes} trucks={trucks} scheduleDate={day.scheduleDate} scheduleDateFormatted={day.scheduleDateFormatted} scheduleDateFormattedSmall={day.scheduleDateFormattedSmall} eventDayTotals={day.eventJobTotals} jobs={day.jobs} saveDayOrder={saveDayOrder} saveJob={updateJob} updateDay={updateDay} />
                    </div>
                )}
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );

}

export default ScheduleEventData;